// mission-root-post.ts — P19a: the mission's ONE Mattermost root post, and
// the file that records its id.
//
// "0 caller" #11, found while wiring #10 (`mattermostTransport` never passed
// by run-command-cli-wiring.ts). mattermost-command-parser.ts rejects any
// post whose thread root != `expectedRootId` with `wrong_thread`, and
// directive-inbox-mattermost-sync.ts takes that id as a required input —
// but NOTHING in production ever called `deliverRootPostIntent` or
// `recordOutboxIntent`, so no mission ever had a root post to thread under.
// Wiring the transport without this would have produced a transport that
// runs every tick and rejects 100% of directives: inert machinery with
// green tests, the exact shape already paid for in Bugs G/I/J/N.
//
// Idempotency is a SAFETY property here, not an optimisation: F06 forbids a
// second root (it would split the mission's conversation and strand every
// reply filed under the first one). The recorded file is the source of
// truth; a failed POST records NOTHING, so the next tick retries rather
// than binding the mission to a root id that may not exist.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { deliverRootPostIntent, type RootPostSender } from "./mattermost-root-post-manager.js";

const ROOT_POST_FILE_NAME = "mission-root-post.json";

export interface MissionRootPostRecord {
  root_post_id: string;
  channel_id: string;
  created_at: string;
}

function rootPostPath(controlRoot: string): string {
  return join(controlRoot, ROOT_POST_FILE_NAME);
}

/**
 * Reads the recorded root post. `undefined` means "this mission has no root
 * post yet" — never "any thread is acceptable": callers that need an
 * `expectedRootId` must treat it as a reason to skip the directive poll
 * entirely, since an absent root cannot be matched against.
 */
export async function readMissionRootPost(
  controlRoot: string,
): Promise<MissionRootPostRecord | undefined> {
  const path = rootPostPath(controlRoot);
  if (!(await pathExists(path))) return undefined;
  try {
    const parsed = await readJson<Partial<MissionRootPostRecord>>(path);
    if (typeof parsed.root_post_id !== "string" || parsed.root_post_id.length === 0) {
      return undefined;
    }
    if (typeof parsed.channel_id !== "string" || parsed.channel_id.length === 0) {
      return undefined;
    }
    return {
      root_post_id: parsed.root_post_id,
      channel_id: parsed.channel_id,
      created_at: typeof parsed.created_at === "string" ? parsed.created_at : "",
    };
  } catch {
    // A corrupt file reads as "no root yet" — the next tick posts a fresh
    // root rather than threading replies under an unparseable id.
    return undefined;
  }
}

export interface EnsureMissionRootPostInput {
  controlRoot: string;
  missionId: string;
  channelId: string;
  message: string;
  sender: RootPostSender;
  /** Correlation marker for response-lost recovery inside deliverRootPostIntent. */
  correlationId?: string;
  now?: () => Date;
}

export interface EnsureMissionRootPostResult {
  /** Undefined when the POST could not be confirmed — caller must retry later. */
  rootPostId?: string;
  /** True only when THIS call created the root (false when reusing the record). */
  created: boolean;
}

/**
 * Ensures the mission has exactly one root post, returning its id.
 *
 * Reuses the recorded id when one exists (never a second `createPost`).
 * When the send cannot be confirmed — `deliverRootPostIntent` returns
 * `uncertain` after its own correlation-marker recovery failed — nothing is
 * recorded and `rootPostId` is undefined: the outbox intent stays as
 * backlog and a later tick tries again. Binding the mission to an
 * unconfirmed id would be worse than having none, because every subsequent
 * reply would be silently rejected as `wrong_thread`.
 */
export async function ensureMissionRootPost(
  input: EnsureMissionRootPostInput,
): Promise<EnsureMissionRootPostResult> {
  const existing = await readMissionRootPost(input.controlRoot);
  if (existing !== undefined) {
    return { rootPostId: existing.root_post_id, created: false };
  }

  const now = input.now ?? (() => new Date());
  // Event id is derived from the mission so a retry after a lost response
  // reuses the SAME outbox op (and therefore the same correlation marker)
  // instead of piling up one pending op per tick.
  const eventId = `root-post-${input.missionId}`;

  const delivered = await deliverRootPostIntent({
    controlRoot: input.controlRoot,
    channelId: input.channelId,
    eventId,
    missionId: input.missionId,
    sequence: 0,
    message: input.message,
    sender: input.sender,
    ...(input.correlationId ? { correlationId: input.correlationId } : {}),
    now: input.now,
  });

  if (delivered.status !== "sent" || delivered.remoteId === undefined) {
    return { created: false };
  }

  const record: MissionRootPostRecord = {
    root_post_id: delivered.remoteId,
    channel_id: input.channelId,
    created_at: now().toISOString(),
  };
  await writeFileDurable(
    rootPostPath(input.controlRoot),
    Buffer.from(`${JSON.stringify({ schema_version: 1, ...record }, null, 2)}\n`, "utf8"),
  );
  return { rootPostId: record.root_post_id, created: true };
}
